import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, Row } from 'react-table';
import NewRegisterBtn from '../../../../../components/Buttons/NewRegisterBtn';
import { backendErrorNotification } from '../../../../../components/Notification';
import NoData from '../../../../../components/NoData';
import TablePaginator from '../../../../../components/TablePaginator';
import {
  ActionButtonsContainer,
  DeleteIconTable,
  EditIconTable,
} from '../../../../../components/TablePaginator/styles';
import HttpStatus from '../../../../../enums/httpStatus';
import i18n from '../../../../../i18n';
import { motivesByActionsReq } from '../../../../../services/requests';
import * as TP from '../../../../../types/apiResponseTypes';
import CreateOrEditMotiveByAction from './Components/CreateOrEditMotiveByAction/index';
import DeleteMotiveByAction from './Components/DeleteMotiveByAction/index';
import * as s from './Components/styles';
import Loader from './Loader';

const MotivesByActions = () => {
  const { t } = useTranslation();

  const [waitingGetMotiveByActions, setWaitingGetMotiveByActions] = useState(false);
  const [motiveByActions, setMotiveByActions] = useState<TP.MotiveByAction[]>([]);
  const [itemSelected, setItemSelected] = useState<TP.MotiveByAction | null>(null);
  const [showModalCreateOrEdit, setShowModalCreateOrEdit] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);

  const reqGetMotiveByActions = useCallback(async () => {
    setWaitingGetMotiveByActions(true);

    const source = motivesByActionsReq.axios.CancelToken.source();

    try {
      const res = await motivesByActionsReq.index(source.token);

      if (res.status === HttpStatus.OK) {
        const { data }: { data: TP.MotiveByAction[] } = res.data;
        setMotiveByActions(data || []);
      } else {
        setMotiveByActions([]);
        throw res;
      }
    } catch (err) {
      if (!motivesByActionsReq.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setWaitingGetMotiveByActions(false);
    }

    return () => {
      source.cancel('Component got unmounted');
    };
  }, []);

  useEffect(() => {
    reqGetMotiveByActions();
  }, [reqGetMotiveByActions]);

  const handleCreate = () => {
    setItemSelected(null);
    setShowModalCreateOrEdit(true);
  };

  const handleTableEditBtn = (row: Row<TP.MotiveByAction>) => {
    setItemSelected(row.original);
    setShowModalCreateOrEdit(true);
  };

  const handleTableDeleteBtn = (row: Row<TP.MotiveByAction>) => {
    setItemSelected(row.original);
    setShowModalDelete(true);
  };

  const actionsButtons = (row: Row<TP.MotiveByAction>) => (
    <ActionButtonsContainer>
      <EditIconTable onClick={() => handleTableEditBtn(row)} />
      <DeleteIconTable onClick={() => handleTableDeleteBtn(row)} />
    </ActionButtonsContainer>
  );

  const columns: Column<TP.MotiveByAction>[] = useMemo(
    () => [
      {
        Header: t('action') as string,
        accessor: 'actionLabel',
      },
      {
        Header: t('motives') as string,
        accessor: 'motives',
        Cell: (cell: any) => (
          <s.MotivesList>
            {(cell.value || []).map((motive: { id: number; description: string }) => (
              <span key={motive.id}>{motive.description}</span>
            ))}
          </s.MotivesList>
        ),
      },
      {
        id: 'actions',
        Cell: ({ row }: any) => actionsButtons(row),
      },
    ],
    [i18n.language],
  );

  return (
    <>
      <NewRegisterBtn
        onClick={handleCreate}
        disabled={waitingGetMotiveByActions}
        style={{ marginLeft: '0', marginBottom: '1rem' }}
      />

      {showModalCreateOrEdit && (
        <CreateOrEditMotiveByAction
          closeModal={() => setShowModalCreateOrEdit(false)}
          reqGetAllMotiveByActions={() => reqGetMotiveByActions()}
          selectedItem={itemSelected}
          usedActionIds={motiveByActions.map((item) => item.actionId)}
        />
      )}

      {showModalDelete && itemSelected && (
        <DeleteMotiveByAction
          itemSelected={itemSelected}
          closeModal={() => setShowModalDelete(false)}
          reqGetAllMotiveByActions={() => reqGetMotiveByActions()}
        />
      )}

      <Loader isLoading={waitingGetMotiveByActions}>
        {motiveByActions.length >= 1 ? (
          <TablePaginator data={motiveByActions} columns={columns} globalFiltering />
        ) : (
          <NoData />
        )}
      </Loader>
    </>
  );
};

export default MotivesByActions;
